<template>
  <b-modal
    v-model="visible"
    size="lg"
    scrollable
    centered
    hide-footer
    :title="t('journeyEditStops')"
    :dir="isHebrew ? 'rtl' : 'ltr'"
    modal-class="journey-stops-editor-modal"
  >
    <p class="stops-editor-hint">{{ t('journeyEditStopsHint', { days: dayCount }) }}</p>

    <ol class="stops-editor-list">
      <li v-for="(stop, index) in previewStops" :key="stop.firstDay" class="stops-editor-stop">
        <div class="stops-editor-head">
          <span class="stops-editor-number">{{ index + 1 }}</span>
          <!-- Empty: the automatic name, shown as the placeholder. -->
          <input
            type="text"
            class="stops-editor-name"
            :value="draft.names[stop.firstDay] || ''"
            :placeholder="automatic[stop.firstDay] && automatic[stop.firstDay].name"
            :maxlength="nameMaxLength"
            :aria-label="t('posterEditStopName')"
            @input="setName(stop, $event.target.value)"
          >
        </div>

        <div class="stops-editor-days">
          <div class="stops-editor-stepper">
            <button
              type="button"
              :aria-label="t('journeyStopLessDays')"
              :disabled="stop.firstDay === stop.lastDay"
              @click="resize(index, -1)"
            >−</button>
            <span class="stops-editor-count">
              <strong>{{ stop.lastDay - stop.firstDay + 1 }}</strong>
              {{ stop.firstDay === stop.lastDay ? t('posterDay') : t('posterDays') }}
            </span>
            <button
              type="button"
              :aria-label="t('journeyStopMoreDays')"
              :disabled="index === previewStops.length - 1"
              @click="resize(index, 1)"
            >+</button>
          </div>
          <span class="stops-editor-range">{{ dayRange(stop) }}</span>
          <button v-if="index > 0" type="button" class="stops-editor-remove" @click="remove(index)">
            {{ t('journeyRemoveStop') }}
          </button>
        </div>

        <div class="stops-editor-photos">
          <button
            type="button"
            :class="['stops-editor-photo', 'stops-editor-photo-auto', { 'is-picked': !draft.photos[stop.firstDay] }]"
            @click="pickPhoto(stop, null)"
          >
            <img
              v-if="automatic[stop.firstDay] && automatic[stop.firstDay].photo"
              :src="$store.getters.getResizeImageForMobileDevices(automatic[stop.firstDay].photo)"
              alt=""
              loading="lazy"
              @error="useFullImage($event, automatic[stop.firstDay].photo)"
            >
            <span class="stops-editor-photo-label">{{ t('posterEditPhotoAuto') }}</span>
          </button>
          <button
            v-for="option in photoOptions(stop)"
            :key="option.url"
            type="button"
            :title="option.label"
            :class="['stops-editor-photo', { 'is-picked': draft.photos[stop.firstDay] === option.url }]"
            @click="pickPhoto(stop, option.url)"
          >
            <img
              :src="$store.getters.getResizeImageForMobileDevices(option.url)"
              :alt="option.label"
              loading="lazy"
              @error="useFullImage($event, option.url)"
            >
          </button>
        </div>
      </li>
    </ol>

    <div class="stops-editor-footer">
      <button type="button" class="stops-editor-btn stops-editor-btn-quiet" @click="resetToAutomatic">
        {{ t('posterEditReset') }}
      </button>
      <span v-if="failed" class="stops-editor-error">{{ t('posterEditSaveFailed') }}</span>
      <div class="stops-editor-actions">
        <button type="button" class="stops-editor-btn" @click="visible = false">{{ t('posterEditCancel') }}</button>
        <button type="button" class="stops-editor-btn stops-editor-btn-primary" :disabled="saving" @click="save">
          {{ saving ? t('posterEditSaving') : t('posterEditSave') }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { translationMixin } from "@/mixins/translationMixin";
import {
  buildPosterStops, posterPhotoOptions, resizeStopSplits, removeStopSplit, carryStopKeys,
  POSTER_SETTINGS_VERSION, POSTER_NAME_MAX_LENGTH,
} from "@/utils/posterStops";
import { designSettings } from "@/utils/rideDesigns";

/**
 * The ride manager's editor for the journey design's stops ("our stops on
 * the trip"): how many days each stop covers, its name and its photo. The
 * stops start as the poster's automatic ones and are saved in the ride's
 * presentation_settings.journey, in the poster's shape (splits, photos,
 * names), so the two designs can be cut differently.
 *
 * Works on a draft and saves it all at once; a name or photo stays with its
 * stop when the days around it change (carryStopKeys).
 *
 * Open with this.$refs.<ref>.open().
 */
export default {
  name: 'JourneyStopsEditor',
  mixins: [translationMixin],
  props: {
    ride: { type: Object, required: true },
    // allRide.attractionsByDay
    days: { type: Array, default: () => [] },
  },
  data() {
    return {
      visible: false,
      draft: { splits: [1], splitsEdited: false, photos: {}, names: {} },
      nameMaxLength: POSTER_NAME_MAX_LENGTH,
      saving: false,
      failed: false,
    };
  },
  computed: {
    dayCount() {
      return this.days.length;
    },
    // The stops as they will look, with the draft.
    previewStops() {
      return buildPosterStops(this.days, { settings: this.draft });
    },
    // What each stop shows when the manager picks or types nothing.
    automatic() {
      const stops = {};
      buildPosterStops(this.days, { settings: { splits: this.draft.splits } })
        .forEach(stop => { stops[stop.firstDay] = stop; });
      return stops;
    },
    draftSettings() {
      const settings = {};
      if (this.draft.splitsEdited) settings.splits = this.draft.splits.slice();
      const photos = Object.assign({}, this.draft.photos);
      const names = {};
      Object.keys(this.draft.names).forEach(day => {
        const name = (this.draft.names[day] || '').trim();
        if (name) names[day] = name;
      });
      if (Object.keys(photos).length) settings.photos = photos;
      if (Object.keys(names).length) settings.names = names;
      return Object.keys(settings).length ? Object.assign({ v: POSTER_SETTINGS_VERSION }, settings) : null;
    },
  },
  methods: {
    open() {
      const saved = designSettings(this.ride, 'journey', POSTER_SETTINGS_VERSION);
      const current = buildPosterStops(this.days, { settings: saved });
      this.draft = {
        splits: current.map(stop => stop.firstDay),
        splitsEdited: !!(saved && saved.splits),
        photos: Object.assign({}, (saved && saved.photos) || {}),
        names: Object.assign({}, (saved && saved.names) || {}),
      };
      this.failed = false;
      this.visible = true;
    },
    // New splits, with every stop's name and photo carried along.
    setSplits(splits, removedIndex = -1) {
      const old = this.draft.splits;
      this.draft = {
        splits,
        splitsEdited: true,
        photos: carryStopKeys(this.draft.photos, old, splits, removedIndex),
        names: carryStopKeys(this.draft.names, old, splits, removedIndex),
      };
    },
    resize(index, delta) {
      const splits = resizeStopSplits(this.draft.splits, this.dayCount, index, delta);
      // A day more can swallow the next stop.
      const removed = splits.length < this.draft.splits.length ? index + 1 : -1;
      this.setSplits(splits, removed);
    },
    remove(index) {
      this.setSplits(removeStopSplit(this.draft.splits, index), index);
    },
    dayRange(stop) {
      return stop.firstDay === stop.lastDay
        ? this.t('dayNumber', { number: stop.firstDay })
        : this.t('posterDayRange', { from: stop.firstDay, to: stop.lastDay });
    },
    photoOptions(stop) {
      return posterPhotoOptions(this.days, stop.firstDay, stop.lastDay);
    },
    pickPhoto(stop, url) {
      const photos = Object.assign({}, this.draft.photos);
      if (url) photos[stop.firstDay] = url;
      else delete photos[stop.firstDay];
      this.draft = Object.assign({}, this.draft, { photos });
    },
    setName(stop, name) {
      const names = Object.assign({}, this.draft.names);
      if (name.trim()) names[stop.firstDay] = name;
      else delete names[stop.firstDay];
      this.draft = Object.assign({}, this.draft, { names });
    },
    // The resized copy is not there for every upload.
    useFullImage(event, url) {
      if (event.target.src !== url) event.target.src = url;
    },
    resetToAutomatic() {
      this.draft = {
        splits: buildPosterStops(this.days).map(stop => stop.firstDay),
        splitsEdited: false,
        photos: {},
        names: {},
      };
    },
    async save() {
      this.saving = true;
      this.failed = false;
      const saved = await this.$store.dispatch('saveRidePresentation', {
        rideId: this.ride.id,
        design: 'journey',
        sections: { journey: this.draftSettings },
      });
      this.saving = false;
      if (saved) this.visible = false;
      else this.failed = true;
    },
  },
};
</script>

<style scoped>
/* Sizes in px: the app's root font is 10px, so rem here would be tiny. */
.stops-editor-hint {
  margin: 0 0 16px;
  font-size: 15px;
  color: #6e6e73;
}

.stops-editor-list {
  display: grid;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.stops-editor-stop {
  padding: 16px;
  border: 1px solid #ececef;
  border-radius: 20px;
  background: #fff;
}

.stops-editor-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.stops-editor-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 11px;
  background: var(--primary-background, #07a0c3);
  color: var(--primary-text-color, #fff);
  font-size: 15px;
  font-weight: 800;
}

.stops-editor-name {
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid #e3e3e6;
  border-radius: 12px;
  font: inherit;
  font-size: 16px;
  font-weight: 700;
  color: #0d1117;
}

.stops-editor-name::placeholder {
  color: #0d1117;
  opacity: 0.5;
}

.stops-editor-name:focus {
  outline: none;
  border-color: var(--primary-color, #07a0c3);
}

.stops-editor-days {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
  margin: 12px 0;
}

.stops-editor-stepper {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(7, 160, 195, 0.1);
  background: color-mix(in srgb, var(--primary-color, #07a0c3) 11%, #fff);
}

.stops-editor-stepper button {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 50%;
  background: #fff;
  color: var(--primary-color, #07a0c3);
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 6px -3px rgba(0, 0, 0, 0.3);
}

.stops-editor-stepper button:disabled {
  opacity: 0.35;
  cursor: default;
}

.stops-editor-count {
  min-width: 70px;
  font-size: 15px;
  text-align: center;
  color: #0d1117;
}

.stops-editor-count strong {
  font-size: 17px;
}

.stops-editor-range {
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-color, #07a0c3);
}

.stops-editor-remove {
  margin-inline-start: auto;
  padding: 6px 12px;
  border: 0;
  border-radius: 999px;
  background: none;
  color: #b42318;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.stops-editor-remove:hover {
  background: #fdecea;
}

.stops-editor-photos {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 3px 3px 6px;
}

.stops-editor-photo {
  position: relative;
  flex: 0 0 96px;
  height: 72px;
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 12px;
  background: #eef1f5;
  box-shadow: 0 0 0 2px transparent;
  cursor: pointer;
}

.stops-editor-photo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.stops-editor-photo.is-picked {
  box-shadow: 0 0 0 3px var(--primary-color, #07a0c3);
}

.stops-editor-photo-label {
  position: absolute;
  inset: auto 0 0;
  padding: 3px 0;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  /* Centred with flex: the app's Hebrew rule forces text-align: right. */
  display: flex;
  justify-content: center;
}

.stops-editor-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 18px;
  padding: 14px 0;
  border-top: 1px solid #ececef;
  background: #f6f5f2;
  /* Covers the modal's bottom padding, so nothing scrolls by under it. */
  box-shadow: 0 24px 0 #f6f5f2;
}

.stops-editor-actions {
  display: flex;
  gap: 8px;
}

.stops-editor-btn {
  padding: 10px 20px;
  border: 1px solid #e3e3e6;
  border-radius: 999px;
  background: #fff;
  color: #0d1117;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}

.stops-editor-btn-quiet {
  border-color: transparent;
  color: var(--primary-color, #07a0c3);
}

.stops-editor-btn-primary {
  border-color: transparent;
  background: var(--primary-background, #07a0c3);
  color: var(--primary-text-color, #fff);
}

.stops-editor-btn:disabled {
  opacity: 0.6;
  cursor: progress;
}

.stops-editor-error {
  font-size: 14px;
  font-weight: 600;
  color: #b42318;
}
</style>

<style>
/* The modal's own frame (rendered outside this component's scope). */
.journey-stops-editor-modal .modal-content {
  border: 0;
  border-radius: 24px;
  overflow: hidden;
}

.journey-stops-editor-modal .modal-header {
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid #ececef;
}

.journey-stops-editor-modal .modal-title {
  font-size: 20px;
  font-weight: 800;
  color: #0d1117;
}

.journey-stops-editor-modal .modal-header .close {
  margin: 0;
  margin-inline-start: auto;
  padding: 0 4px;
  font-size: 30px;
  line-height: 1;
}

.journey-stops-editor-modal .modal-body {
  padding: 18px 22px 24px;
  background: #f6f5f2;
}
</style>
